import api from "@/services/api";

import type { MentionCandidate } from "../hooks/useMentionCandidates";
import { canPreview, contentTypeFromName, type PreviewFile } from "./FilePreviewDialog";
import { PAGE_COUNT_TIMEOUT_MS, requestTimedOut, TIMED_OUT, type PageCount } from "./pages";
import { previewKind } from "./preview/kinds";

/** A file on the thread, as the attach routes answer. */
export interface ThreadFileInfo {
  id: number;
  name: string;
  contentType: string;
}

/** Adds one of the application's documents to the thread by reference (no upload). */
export async function attachDocument(
  threadId: number,
  document: { contextId: number; id: number; statement?: boolean }
): Promise<ThreadFileInfo> {
  const { data } = await api.post<{ id: number; name: string; content_type: string }>(
    `/agent/threads/${threadId}/documents/`,
    {
      application_id: document.contextId,
      document_id: document.id,
      statement: !!document.statement,
    }
  );
  return { id: data.id, name: data.name, contentType: data.content_type };
}

/** A short-lived link to a thread file, or null when there is none to give. */
export async function threadFileUrl(
  threadId: number,
  threadFileId: number
): Promise<string | null> {
  try {
    const { data } = await api.get<{ url: string }>(
      `/agent/threads/${threadId}/files/${threadFileId}/preview/`
    );
    return data.url || null;
  } catch {
    return null;
  }
}

/**
 * A thread file's pages, as the server reads them for a send: null for one that is not a PDF or
 * does not open, or when the server can't say; TIMED_OUT when it does not answer in time.
 */
export async function threadFilePages(threadId: number, threadFileId: number): Promise<PageCount> {
  try {
    const { data } = await api.get<{ pages: number | null }>(
      `/agent/threads/${threadId}/files/${threadFileId}/pages/`,
      { timeout: PAGE_COUNT_TIMEOUT_MS }
    );
    return typeof data.pages === "number" ? data.pages : null;
  } catch (error) {
    return requestTimedOut(error) ? TIMED_OUT : null;
  }
}

/**
 * A type an own-host preview may render, from the file's own first bytes, never its name: a PDF or
 * a raster image. Null for everything else (HTML, SVG, text, anything unknown), which downloads.
 */
export function sniffPreviewType(bytes: Uint8Array): string | null {
  const starts = (...sig: number[]) => sig.every((byte, index) => bytes[index] === byte);
  const ascii = (offset: number, text: string) =>
    [...text].every((char, index) => bytes[offset + index] === char.charCodeAt(0));
  if (ascii(0, "%PDF-")) return "application/pdf";
  if (starts(0x89, 0x50, 0x4e, 0x47)) return "image/png";
  if (starts(0xff, 0xd8, 0xff)) return "image/jpeg";
  if (ascii(0, "GIF8")) return "image/gif";
  if (ascii(0, "RIFF") && ascii(8, "WEBP")) return "image/webp";
  return null;
}

/** Saves bytes as a file, never rendering them: an untyped blob behind a download link. */
export function downloadBytes(bytes: BlobPart, name: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name || "document";
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** An @ row's eye: a preview to show, a download that already started, or nothing to give. */
export type MentionPreview = { file: PreviewFile } | { downloaded: true } | null;

/**
 * Whether an @ row offers its eye: a chat file, a Files panel file with a preview (see
 * `previewKind`), or a linked document whose name looks previewable.
 */
export function canPreviewMention(candidate: MentionCandidate): boolean {
  if (candidate.kind === "workspace_file") return previewKind(candidate.file) !== null;
  return canPreview(
    candidate.kind === "thread_file"
      ? candidate.contentType
      : candidate.url
        ? contentTypeFromName(candidate.name)
        : undefined
  );
}

/** The origins whose pages run with the app's cookies: this page's and the API's. */
function ownOrigins(): Set<string> {
  const page = window.location.origin;
  return new Set([page, new URL(api.defaults.baseURL ?? "/", window.location.href).origin]);
}

/**
 * What the eye does for an @ row (a Files panel file has its own previews, `WorkspacePreviewDialog`).
 * A chat file is typed by the server. An application document depends on where its link points:
 * - our own hosts (the media fallback when a link can't be presigned), where a document would run
 *   with the app's cookies: the bytes are fetched (CORS already allows the app). A PDF or a raster
 *   image (by its bytes, never its name) renders from a blob of that type; anything else (HTML,
 *   SVG, text) is saved as a download and never rendered;
 * - storage elsewhere (a presigned S3 link): shown at its URL, as the app's other viewers do. It
 *   runs in the storage's own origin, so it can't reach the app, and needs no CORS.
 */
export async function mentionPreview(
  threadId: number,
  candidate: Exclude<MentionCandidate, { kind: "workspace_file" }>
): Promise<MentionPreview> {
  if (candidate.kind === "thread_file") {
    const url = await threadFileUrl(threadId, candidate.id);
    return url ? { file: { name: candidate.name, url, contentType: candidate.contentType } } : null;
  }
  if (!candidate.url) return null;
  const link = new URL(candidate.url, window.location.href);
  if (!ownOrigins().has(link.origin)) {
    // The name picks the viewer only (image or frame); the frame is sandboxed unless it is a PDF.
    const contentType = contentTypeFromName(candidate.name);
    return contentType ? { file: { name: candidate.name, url: link.href, contentType } } : null;
  }
  try {
    const response = await fetch(link.href, { credentials: "include" });
    if (!response.ok) return null;
    const bytes = new Uint8Array(await response.arrayBuffer());
    const contentType = sniffPreviewType(bytes);
    if (!contentType) {
      downloadBytes(bytes, candidate.name);
      return { downloaded: true };
    }
    const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
    return { file: { name: candidate.name, url, contentType } };
  } catch {
    return null;
  }
}
